import { useEffect, useLayoutEffect, useRef, useState } from "react";

const formatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

function formatParts(date: Date) {
  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return {
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
    dayPeriod: get("dayPeriod"),
  };
}

function RollingChar({ char }: { char: string }) {
  const [shown, setShown] = useState(char);
  const [leaving, setLeaving] = useState<string | null>(null);
  const rolled = useRef(false);

  useLayoutEffect(() => {
    if (char === shown) return;
    rolled.current = true;
    setLeaving(shown);
    setShown(char);
  }, [char, shown]);

  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(() => setLeaving(null), 400);
    return () => window.clearTimeout(id);
  }, [leaving]);

  return (
    <span className="digit-slot">
      {leaving && leaving !== shown ? (
        <span className="digit digit-out">{leaving}</span>
      ) : null}
      <span className={rolled.current ? "digit digit-in" : "digit"}>{shown}</span>
    </span>
  );
}

function RollingText({ value }: { value: string }) {
  return (
    <span className="inline-flex">
      {value.split("").map((char, index) => (
        <RollingChar key={index} char={char} />
      ))}
    </span>
  );
}

export function Clock() {
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  const parts = now ? formatParts(now) : null;
  const spoken = parts
    ? `Austin ${parts.hour}:${parts.minute}:${parts.second} ${parts.dayPeriod}`
    : "Austin";

  return (
    <time
      dateTime={now?.toISOString()}
      aria-label={spoken}
      className="text-xs tabular-nums tracking-wide text-muted"
    >
      <span>Austin</span>
      <span className="mx-1.5 text-faint">·</span>
      {parts ? (
        <span aria-hidden="true">
          <RollingText value={parts.hour} />
          <span className="clock-colon">:</span>
          <RollingText value={parts.minute} />
          <span className="clock-colon">:</span>
          <RollingText value={parts.second} />
          <span className="ml-1">
            <RollingText value={parts.dayPeriod} />
          </span>
        </span>
      ) : (
        <span className="text-faint">—:—:—</span>
      )}
    </time>
  );
}
